import React from 'react'

import {
  Heading,
  Icon,
  Loader,
  ModalBox,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { ArrowShortLeftIcon } from '@fourthwall/icons'

import { SectionItem } from '../SectionItemV2'
import { IFramePreview } from './components/IFramePreview/IFramePreview'
import { useRecommendations } from './hooks/useRecommendations'
import * as S from './styled'
import type { CustomSection, GroupedSection, Section } from './types'

type Props = {
  blockId: string
  sections?: GroupedSection[]
  selectedSection: Section | CustomSection | null
  onCreateLayoutSection: (
    sectionType: string,
    blockId: string,
    platformAppId?: string,
    presetName?: string,
  ) => void
  filterSections: string[]
  onSectionSelect: (section: Section | CustomSection | null) => void
}

export const AddSectionModalView = ({
  blockId,
  sections,
  selectedSection,
  onCreateLayoutSection,
  filterSections,
  onSectionSelect,
}: Props) => {
  const { close } = useModalContext()
  const media = useMediaQueryContext()

  const { recommendations } = useRecommendations({ sections })

  const renderLoader = () => {
    if (sections) return null

    return (
      <S.LoaderWrapper>
        <Loader size="large" />
      </S.LoaderWrapper>
    )
  }

  const renderSectionItems = (sections: Section[]) => {
    const filteredSections = sections.filter((s) => !filterSections.includes(s.sectionType))

    return filteredSections.map((item) => (
      <SectionItem
        key={item.sectionType}
        section={item}
        active={selectedSection === item}
        onClick={() => onSectionSelect(item)}
      />
    ))
  }

  const renderRecommendedSection = () => {
    if (!recommendations || blockId !== 'content_for_index') return null

    return (
      <S.SectionWrapper>
        <S.SectionContent>
          <SectionItem
            section={{ sectionType: 'recommended', name: 'Recommended', presets: [] }}
            active={selectedSection === 'Recommended'}
            onClick={() => onSectionSelect('Recommended')}
          />
        </S.SectionContent>
      </S.SectionWrapper>
    )
  }

  const renderPreview = (section: Section | CustomSection) => {
    if (section === 'Recommended') {
      if (!recommendations) return null

      return (
        <S.RecommendedContainer>
          {recommendations?.map(({ group, sections }) => (
            <S.RecommendedGroup>
              <S.RecommendedGroupTitle>{group}</S.RecommendedGroupTitle>
              <S.PreviewsGrid>
                {sections.map((section) => (
                  <IFramePreview
                    key={section.sectionType}
                    url={section.presets[0].previewUrl}
                    fluid
                    align="start"
                    onClick={() =>
                      onCreateLayoutSection(
                        section.sectionType,
                        blockId,
                        section.platformAppId,
                        section.presets[0].name,
                      )
                    }
                  />
                ))}
              </S.PreviewsGrid>
            </S.RecommendedGroup>
          ))}
        </S.RecommendedContainer>
      )
    }

    if (section.presets.length === 1) {
      return (
        <S.SinglePreviewContainer>
          {section.presets.map((preset) => (
            <IFramePreview
              key={preset.name}
              url={preset.previewUrl}
              onClick={() =>
                onCreateLayoutSection(
                  section.sectionType,
                  blockId,
                  section.platformAppId,
                  preset.name,
                )
              }
            />
          ))}
        </S.SinglePreviewContainer>
      )
    }

    return (
      <S.PreviewsGrid>
        {section.presets.map((preset) => (
          <IFramePreview
            key={preset.name}
            url={preset.previewUrl}
            onClick={() =>
              onCreateLayoutSection(
                section.sectionType,
                blockId,
                section.platformAppId,
                preset.name,
              )
            }
          />
        ))}
      </S.PreviewsGrid>
    )
  }

  const renderContent = () => {
    if (!sections) return null

    if (media.tablet) {
      return (
        <S.Content>
          <S.Sidebar>
            <S.HeadingContainer>
              <Heading type="h3" weight="semibold">
                Add new section
              </Heading>
            </S.HeadingContainer>
            {renderRecommendedSection()}
            {sections.map(({ group, sections }) => (
              <S.SectionWrapper key={group}>
                <S.SectionHeader>{group}</S.SectionHeader>
                <S.SectionContent>{renderSectionItems(sections)}</S.SectionContent>
              </S.SectionWrapper>
            ))}
          </S.Sidebar>
          {selectedSection && (
            <S.PreviewsContainer>{renderPreview(selectedSection)}</S.PreviewsContainer>
          )}
        </S.Content>
      )
    }

    if (selectedSection) {
      return (
        <S.Sidebar>
          <S.HeadingContainer onClick={() => onSectionSelect(null)}>
            <Icon component={ArrowShortLeftIcon} height={16} />
            <Heading type="h3" weight="semibold">
              {selectedSection === 'Recommended' ? selectedSection : selectedSection.name}
            </Heading>
          </S.HeadingContainer>
          <S.PreviewsContainer>{renderPreview(selectedSection)}</S.PreviewsContainer>
        </S.Sidebar>
      )
    }

    return (
      <S.Sidebar>
        <S.HeadingContainer>
          <Heading type="h3" weight="semibold">
            Add new section
          </Heading>
        </S.HeadingContainer>
        {renderRecommendedSection()}
        {sections.map(({ group, sections }) => (
          <S.SectionWrapper key={group}>
            <S.SectionHeader>{group}</S.SectionHeader>
            <S.SectionContent>{renderSectionItems(sections)}</S.SectionContent>
          </S.SectionWrapper>
        ))}
      </S.Sidebar>
    )
  }

  return (
    <ModalBox maxWidth={1400} disableScrolling withoutPadding onClose={close}>
      {renderLoader()}
      {renderContent()}
    </ModalBox>
  )
}
